import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import {
  CashRegisterMovement,
  CreateMovementRequest,
  RegisterSession,
  RegisterSessionPayment,
  SessionLedgerEntry,
  SessionLedgerQuery,
} from "./pos.type";

export const RegisterSessionApi = createApi({
  reducerPath: "registerSessionApi",
  tagTypes: [
    "registerSession",
    "registerSessionPayment",
    "registerSessionLedger",
  ],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    openSession: build.mutation<
      RegisterSession,
      { restaurantId: string; data: { openingBalance: number; notes?: string } }
    >({
      query: ({ restaurantId, data }) => ({
        url: `restaurants/${restaurantId}/register_sessions`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["registerSession"],
    }),

    closeSession: build.mutation<
      RegisterSession,
      { sessionUuid: string; data: { actualCash: number; notes?: string } }
    >({
      query: ({ sessionUuid, data }) => ({
        url: `register_sessions/${sessionUuid}/close`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["registerSession"],
    }),

    getSessions: build.query<
      PaginationResults<RegisterSession>,
      {
        restaurantId: string;
        query?: {
          page?: number;
          limit?: number;
          "openedAt[after]"?: string;
          "openedAt[before]"?: string;
          "closedAt[after]"?: string;
          "closedAt[before]"?: string;
        };
      }
    >({
      query: ({ restaurantId, query }) => ({
        url: `restaurants/${restaurantId}/register_sessions`,
        method: "GET",
        params: query,
      }),
      providesTags: ["registerSession"],
      transformResponse: (data: CollectionData<RegisterSession>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getSession: build.query<RegisterSession, { sessionUuid: string }>({
      query: ({ sessionUuid }) => `register_sessions/${sessionUuid}`,
      providesTags: ["registerSession"],
    }),

    getSessionPayments: build.query<
      PaginationResults<RegisterSessionPayment>,
      {
        sessionUuid: string;
        query?: { page?: number; limit?: number; orderNumber?: string };
      }
    >({
      query: ({ sessionUuid, query }) => ({
        url: `register_sessions/${sessionUuid}/payments`,
        method: "GET",
        params: query,
      }),
      providesTags: ["registerSessionPayment"],
      transformResponse: (data: CollectionData<RegisterSessionPayment>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),

    getOpenSession: build.query<RegisterSession | null, { restaurantId: string }>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `restaurants/${arg.restaurantId}/register_sessions/open`,
          method: "GET",
        });
        if (result.error) {
          if (result.error.status === 404) {
            return { data: null };
          }
          return { error: result.error };
        }
        return { data: result.data as RegisterSession };
      },
      providesTags: ["registerSession"],
    }),

    recordMovement: build.mutation<
      CashRegisterMovement,
      {
        restaurantId: string;
        sessionUuid: string;
        data: CreateMovementRequest;
      }
    >({
      query: ({ restaurantId, sessionUuid, data }) => ({
        url: `restaurants/${restaurantId}/register_sessions/${sessionUuid}/movements`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["registerSession", "registerSessionLedger"],
    }),

    getSessionLedger: build.query<
      PaginationResults<SessionLedgerEntry>,
      { sessionUuid: string; query?: SessionLedgerQuery }
    >({
      query: ({ sessionUuid, query }) => ({
        url: `register_sessions/${sessionUuid}/ledger`,
        method: "GET",
        params: query,
      }),
      providesTags: ["registerSessionLedger"],
      transformResponse: (data: CollectionData<SessionLedgerEntry>) => ({
        results: data["hydra:member"],
        count: data["hydra:totalItems"],
      }),
    }),
  }),
});

export const {
  useOpenSessionMutation,
  useCloseSessionMutation,
  useGetSessionsQuery,
  useGetSessionQuery,
  useGetSessionPaymentsQuery,
  useGetOpenSessionQuery,
  useRecordMovementMutation,
  useGetSessionLedgerQuery,
} = RegisterSessionApi;
